import React, {createContext, useState, useCallback, useContext, useRef, useMemo} from "react";
import PropTypes from "prop-types";

// Utils
import {AuthContext} from "./auth.js";
import useApi from "../hooks/useApi.js";

export const NotificationContext = createContext();

const NotifyProvider = ({children}) => {
  const {currentUser} = useContext(AuthContext);

  const [notifications, setNotifications] = useState(null);

  const {api, loading} = useApi("alerts");

  const call = useRef(false);
  const pending = useRef(null);

  // Updates checksheet object in place
  //    if key is not passed, overrides checksheet object at id
  //      ex: updateChecksheetInfo(checksheet.id, updatedChecksheet)
  //    if key is passed, overrides value at key on checksheet
  //      ex: updateChecksheetInfo(checksheet.id, updatedFrequency, "frequency")
  const updateChecksheetInfo = useCallback((id, val, key = null) => {
    setNotifications(prev => {
      if (!prev) return prev;

      const updated = {...prev};

      const {checksheetsDue, checksheetsOptional, checksheetsExternal} = prev;
      updated.checksheetsDue = checksheetsDue?.map(checksheet => {
        const updatedChecksheet = {...checksheet};
        const {id: checksheetId} = updatedChecksheet;
        if (id === checksheetId) {
          if (!key) return val;
          updatedChecksheet[key] = val;
        }
        return updatedChecksheet;
      });
      updated.checksheetsOptional = checksheetsOptional?.map(checksheet => {
        const updatedChecksheet = {...checksheet};
        const {id: checksheetId} = updatedChecksheet;
        if (id === checksheetId) {
          if (!key) return val;
          updatedChecksheet[key] = val;
        }
        return updatedChecksheet;
      });
      updated.checksheetsExternal = checksheetsExternal?.map(checksheet => {
        const updatedChecksheet = {...checksheet};
        const {id: checksheetId} = updatedChecksheet;
        if (id === checksheetId) {
          if (!key) return val;
          updatedChecksheet[key] = val;
        }
        return updatedChecksheet;
      });

      return updated;
    });
  }, []);

  const getNotifications = useCallback(
    (slug = null) => {
      if (!slug) {
        setNotifications(null);
        return;
      }

      // A fetch is already in flight; remember the latest request and run it once
      // the current one settles so concurrent refreshes (e.g. a state change landing
      // mid-fetch) are not silently dropped.
      if (call.current) {
        pending.current = slug;
        return;
      }

      const fetchNotifications = targetSlug => {
        call.current = true;
        api
          .callGet("", {slug: targetSlug, userId: currentUser.publicId})
          .then(({status, data}) => {
            if (status === 200 && data) setNotifications(data);
          })
          .finally(() => {
            call.current = false;
            if (pending.current) {
              const nextSlug = pending.current;
              pending.current = null;
              fetchNotifications(nextSlug);
            }
          });
      };

      fetchNotifications(slug);
    },
    [api, currentUser]
  );

  // Make the provider update only when it should.
  // We only want to force re-renders on dependencies
  const memoedValue = useMemo(
    () => ({
      notifications,
      setNotifications,
      getNotifications,
      isLoading: loading,
      updateChecksheetInfo
    }),
    [getNotifications, notifications, loading, updateChecksheetInfo]
  );

  return (
    <NotificationContext.Provider value={memoedValue}>{children}</NotificationContext.Provider>
  );
};

NotifyProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default NotifyProvider;
